import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Papa from "papaparse";
import { FaFileCsv, FaTimes } from "react-icons/fa";
import { TournamentHeader } from "./GameSessionHeader";
import "./Style.css";

const EXPORT_COLUMNS = [
  { key: "name", label: "Name" },
  { key: "first_name", label: "First Name" },
  { key: "last_name", label: "Last Name" },
  { key: "phone_number", label: "Phone" },
  { key: "tournaments_played", label: "Tournaments Played" },
  { key: "entries", label: "Entries" },
  { key: "free_entries", label: "Free Entries" },
  { key: "deposits", label: "Deposits" },
  { key: "cashed", label: "Cashed" },
  { key: "profit", label: "Profit" },
  { key: "tournament_points", label: "Tournament Points" },
  { key: "record_points", label: "Record Points" },
  { key: "cashes", label: "Cashes" },
  { key: "best_place", label: "Best Place" },
  { key: "average_entry", label: "Average Entry" },
  { key: "cash_return_pct", label: "Cash Return %" },
  { key: "last_played_tournament", label: "Last Played Tournament" },
  { key: "notes", label: "Notes" },
];

const FILTERS = [
  { key: "all", label: "All Tournament Players" },
  { key: "cashed", label: "Players Who Cashed" },
  { key: "not_cashed", label: "Players Without Cashes" },
  { key: "with_free_entries", label: "Players With Free Entries" },
  { key: "profitable", label: "Profitable Players" },
  { key: "unprofitable", label: "Unprofitable Players" },
  { key: "active_since", label: "Active Since" },
  { key: "inactive_since", label: "Inactive Since" },
];

const money = (value) => `$${Math.round(Number(value) || 0).toLocaleString()}`;
const DATE_SORT_KEYS = new Set(["last_played_tournament", "last_record_entered_at"]);

function InitialLoadingScreen() {
  return (
    <div
      className="loading-container"
      style={{ padding: "4rem", textAlign: "center", color: "white" }}
    >
      <h2 style={{ marginBottom: "1.5rem", fontWeight: "700" }}>
        Gathering Data...
      </h2>
      <div style={{ display: "flex", justifyContent: "center" }}>
        <div
          className="spinner"
          style={{
            border: "4px solid rgba(255,255,255,0.1)",
            borderTop: "4px solid #6366f1",
            borderRadius: "50%",
            width: "50px",
            height: "50px",
            animation: "spin 1s linear infinite",
          }}
        ></div>
      </div>
      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

const formatDate = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export default function TournamentPlayers() {
  const { cardroom } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const permission = localStorage.getItem("permission");
  const dealerMode = Number(localStorage.getItem("dealer_mode") || 0);

  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("all");
  const [filterDate, setFilterDate] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "profit", direction: "desc" });
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportColumns, setExportColumns] = useState(EXPORT_COLUMNS.map((column) => column.key));
  const [exportFilter, setExportFilter] = useState("all");
  const [exportFilterDate, setExportFilterDate] = useState("");

  useEffect(() => {
    if (!token) {
      navigate("/");
      return;
    }
    if (dealerMode === 2) {
      navigate(`/${cardroom}/dealerdashboard`);
      return;
    }
    if (!permission?.includes("O") && !permission?.includes("Y")) {
      navigate(`/${cardroom}/game`);
      return;
    }
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  }, [token, cardroom, navigate, dealerMode, permission]);

  useEffect(() => {
    const loadPlayers = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`/${cardroom}/api/tournament-players`);
        setPlayers(data);
      } catch (err) {
        console.error("Failed to load tournament players", err);
      } finally {
        setLoading(false);
      }
    };
    if (token) loadPlayers();
  }, [cardroom, token]);

  const applyPlayerFilter = (rows, selectedFilter, selectedFilterDate, selectedSearch = "") => {
    const search = selectedSearch.trim().toLowerCase();
    const selectedDate = selectedFilterDate ? new Date(`${selectedFilterDate}T00:00:00`) : null;

    return rows.filter((player) => {
      const fullName = `${player.first_name || ""} ${player.last_name || ""}`.trim().toLowerCase();
      const displayName = (player.name || "").toLowerCase();
      const matchesSearch = !search || displayName.includes(search) || fullName.includes(search);
      if (!matchesSearch) return false;

      if (selectedFilter === "cashed") return Number(player.cashes) > 0;
      if (selectedFilter === "not_cashed") return Number(player.cashes) === 0;
      if (selectedFilter === "with_free_entries") return Number(player.free_entries) > 0;
      if (selectedFilter === "profitable") return Number(player.profit) > 0;
      if (selectedFilter === "unprofitable") return Number(player.profit) < 0;
      if (selectedFilter === "active_since") {
        if (!selectedDate || !player.last_played_tournament) return false;
        return new Date(player.last_played_tournament) >= selectedDate;
      }
      if (selectedFilter === "inactive_since") {
        if (!selectedDate) return true;
        if (!player.last_played_tournament) return true;
        return new Date(player.last_played_tournament) < selectedDate;
      }
      return true;
    });
  };

  const filteredPlayers = useMemo(() => {
    return applyPlayerFilter(players, filter, filterDate, searchTerm);
  }, [players, searchTerm, filter, filterDate]);

  const sortRows = (rows) => {
    return [...rows].sort((a, b) => {
      const aValue = sortConfig.key === "name"
        ? a.name || ""
        : DATE_SORT_KEYS.has(sortConfig.key)
          ? new Date(a[sortConfig.key] || 0).getTime()
          : Number(a[sortConfig.key]) || 0;
      const bValue = sortConfig.key === "name"
        ? b.name || ""
        : DATE_SORT_KEYS.has(sortConfig.key)
          ? new Date(b[sortConfig.key] || 0).getTime()
          : Number(b[sortConfig.key]) || 0;
      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  };

  const sortedPlayers = useMemo(() => sortRows(filteredPlayers), [filteredPlayers, sortConfig]);

  const requestSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "desc" ? "asc" : "desc",
    }));
  };

  const downloadCsv = () => {
    const selectedColumns = EXPORT_COLUMNS.filter((column) => exportColumns.includes(column.key));
    const exportRows = sortRows(applyPlayerFilter(players, exportFilter, exportFilterDate));
    const rows = exportRows.map((player) =>
      selectedColumns.reduce((out, column) => {
        out[column.label] = player[column.key] ?? "";
        return out;
      }, {}),
    );
    const csv = Papa.unparse(rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Tournament_Players_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    setShowExportModal(false);
  };

  const summary = players.reduce(
    (acc, player) => {
      acc.players += 1;
      acc.entries += Number(player.entries) || 0;
      acc.freeEntries += Number(player.free_entries) || 0;
      acc.deposits += Number(player.deposits) || 0;
      acc.cashed += Number(player.cashed) || 0;
      acc.profit += Number(player.profit) || 0;
      return acc;
    },
    { players: 0, entries: 0, freeEntries: 0, deposits: 0, cashed: 0, profit: 0 },
  );

  const tableColumns = [
    { key: "name", label: "Player", render: (p) => p.name },
    { key: "tournaments_played", label: "Tournaments", render: (p) => p.tournaments_played || 0 },
    { key: "entries", label: "Entries", render: (p) => p.entries || 0 },
    { key: "free_entries", label: "Free Entries", render: (p) => p.free_entries || 0 },
    { key: "deposits", label: "Deposits", render: (p) => money(p.deposits), color: "#fbbf24" },
    { key: "cashed", label: "Cashed", render: (p) => money(p.cashed), color: "#10b981" },
    { key: "tournament_points", label: "Points", render: (p) => p.tournament_points || 0 },
    { key: "cashes", label: "Cashes", render: (p) => p.cashes || 0 },
    { key: "best_place", label: "Best Place", render: (p) => p.best_place || "-" },
    { key: "average_entry", label: "Avg Entry", render: (p) => money(p.average_entry) },
    { key: "cash_return_pct", label: "Return %", render: (p) => p.cash_return_pct == null ? "-" : `${Number(p.cash_return_pct).toFixed(1)}%` },
    { key: "last_played_tournament", label: "Last Played", render: (p) => formatDate(p.last_played_tournament || p.last_record_entered_at) },
    { key: "profit", label: "Profit", render: (p) => money(p.profit), color: (p) => (Number(p.profit) || 0) >= 0 ? "#10b981" : "#f43f5e" },
  ];

  return (
    <div className="game">
      <TournamentHeader
        activePage="players"
        cardroom={cardroom}
        centerTitle="Tournament Players"
        title="Tournaments"
      />

      <div className="game-container">
        {loading ? (
          <InitialLoadingScreen />
        ) : (
          <>
            <section className="game-section">
              <h2 className="game-section__title">Tournament Player Summary</h2>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: "1rem" }}>
                <Summary title="Players" value={summary.players} />
                <Summary title="Entries" value={summary.entries} />
                <Summary title="Free Entries" value={summary.freeEntries} />
                <Summary title="Deposits" value={money(summary.deposits)} />
                <Summary title="Cashed" value={money(summary.cashed)} />
                <Summary title="Profit" value={money(summary.profit)} color={summary.profit >= 0 ? "#10b981" : "#f43f5e"} />
              </div>
            </section>

            <section className="game-section">
              <h2 className="game-section__title" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem" }}>
                <span>Tournament Players ({sortedPlayers.length})</span>
                <FaFileCsv
                  className="game-icon-btn game-icon-btn--success"
                  onClick={() => {
                    setExportFilter(filter);
                    setExportFilterDate(filterDate);
                    setShowExportModal(true);
                  }}
                  title="Download Tournament Players CSV"
                />
              </h2>

              <div className="game-form" style={{ marginBottom: "1rem", alignItems: "center" }}>
                <input
                  className="game-input"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search tournament players..."
                  style={{ minWidth: 260 }}
                />
                <select className="game-input" value={filter} onChange={(e) => setFilter(e.target.value)} style={{ maxWidth: 240 }}>
                  {FILTERS.map((option) => (
                    <option key={option.key} value={option.key}>{option.label}</option>
                  ))}
                </select>
                {(filter === "active_since" || filter === "inactive_since") && (
                  <input
                    className="game-input"
                    type="date"
                    max={new Date().toISOString().split("T")[0]}
                    value={filterDate}
                    onChange={(e) => setFilterDate(e.target.value)}
                    style={{ maxWidth: 180 }}
                  />
                )}
                {searchTerm && (
                  <button className="game-icon-btn" onClick={() => setSearchTerm("")} title="Clear search">
                    <FaTimes />
                  </button>
                )}
              </div>

              {sortedPlayers.length === 0 ? (
                <p className="game-empty">No tournament players match the current view.</p>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.875rem", minWidth: 1180 }}>
                    <thead>
                      <tr style={{ color: "#94a3b8", borderBottom: "1px solid #334155" }}>
                        {tableColumns.map((column) => (
                          <th
                            key={column.key}
                            onClick={() => requestSort(column.key)}
                            style={{ padding: "0.75rem", textAlign: "center", cursor: "pointer", whiteSpace: "nowrap" }}
                          >
                            {column.label}<SortIndicator active={sortConfig.key === column.key} direction={sortConfig.direction} />
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedPlayers.map((player) => (
                        <tr key={player.id} style={{ borderBottom: "1px solid #0f172a" }}>
                          {tableColumns.map((column) => {
                            const color = typeof column.color === "function" ? column.color(player) : column.color;
                            return (
                              <td
                                key={column.key}
                                style={{ padding: "0.75rem", textAlign: "center", color: color || "#e2e8f0", fontWeight: column.key === "name" || column.key === "profit" ? 800 : 600, whiteSpace: "nowrap" }}
                              >
                                {column.render(player)}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )}
      </div>

      {showExportModal && (
        <>
          <div className="game-modal-overlay" onClick={() => setShowExportModal(false)} />
          <div className="game-modal game-modal--large" style={{ maxWidth: 760 }}>
            <div className="game-modal__header">
              <h3 className="game-modal__title">Download Tournament Players CSV</h3>
              <button type="button" className="game-icon-btn" onClick={() => setShowExportModal(false)}><FaTimes /></button>
            </div>
            <div className="game-modal__body game-modal__body--scroll">
              <div className="game-form-vertical" style={{ marginBottom: "1.25rem" }}>
                <div>
                  <label className="game-label">Player Filter</label>
                  <select className="game-input" value={exportFilter} onChange={(e) => setExportFilter(e.target.value)}>
                    {FILTERS.map((option) => (
                      <option key={option.key} value={option.key}>{option.label}</option>
                    ))}
                  </select>
                </div>
                {(exportFilter === "active_since" || exportFilter === "inactive_since") && (
                  <div>
                    <label className="game-label">{exportFilter === "active_since" ? "Active Since" : "Inactive Since"}</label>
                    <input
                      className="game-input"
                      type="date"
                      max={new Date().toISOString().split("T")[0]}
                      value={exportFilterDate}
                      onChange={(e) => setExportFilterDate(e.target.value)}
                    />
                  </div>
                )}
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", gap: "1rem", alignItems: "center", marginBottom: "0.75rem" }}>
                <label className="game-label" style={{ margin: 0 }}>Columns</label>
                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                  <button type="button" className="game-btn game-btn--sm game-btn--ghost" onClick={() => setExportColumns(EXPORT_COLUMNS.map((column) => column.key))}>Everything</button>
                  <button type="button" className="game-btn game-btn--sm game-btn--ghost" onClick={() => setExportColumns([])}>Nothing</button>
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: "0.65rem" }}>
                {EXPORT_COLUMNS.map((column) => (
                  <label key={column.key} className="game-filter-chip" style={{ minHeight: "3rem", alignItems: "center" }}>
                    <input
                      type="checkbox"
                      checked={exportColumns.includes(column.key)}
                      onChange={(e) => {
                        setExportColumns((prev) =>
                          e.target.checked ? [...prev, column.key] : prev.filter((key) => key !== column.key),
                        );
                      }}
                    />
                    <span>{column.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="game-modal__footer">
              <button type="button" className="game-btn game-btn--ghost" onClick={() => setShowExportModal(false)}>Cancel</button>
              <button type="button" className="game-btn game-btn--primary" onClick={downloadCsv} disabled={exportColumns.length === 0}>Download CSV</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Summary({ title, value, color = "#f8fafc" }) {
  return (
    <div style={{ background: "#1e293b", border: "1px solid #334155", borderRadius: "0.75rem", padding: "1rem", textAlign: "center" }}>
      <div style={{ color: "#94a3b8", fontSize: "0.75rem", fontWeight: 800, textTransform: "uppercase", marginBottom: "0.4rem" }}>{title}</div>
      <div style={{ color, fontSize: "1.35rem", fontWeight: 900 }}>{value}</div>
    </div>
  );
}

function SortIndicator({ active, direction }) {
  if (!active) return null;
  return (
    <span
      aria-hidden="true"
      style={{
        display: "inline-block",
        width: 0,
        height: 0,
        marginLeft: "0.35rem",
        verticalAlign: "middle",
        borderLeft: "4px solid transparent",
        borderRight: "4px solid transparent",
        borderBottom: direction === "asc" ? "6px solid #94a3b8" : "none",
        borderTop: direction === "desc" ? "6px solid #94a3b8" : "none",
      }}
    />
  );
}
